import { memo, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import searchInVehicles, {
    sparePartSearchFields,
} from '../util/searchInVehicles';
import { bicycleSorter } from '../util/motorcycleSorter';
import filterBicycles from '../util/filterBicycles';
import Container from '../../core/components/Container';
import BreadcrumbsViews from '../../core/components/router/BreadcrumbsViews';
import UnhandledError from '../../core/components/UnhandledError';
import useCurrentStore from '../../core/hooks/useCurrentStore';
import useMetaProducts from '../queries/useMetaProducts';
import useEffectWithoutMount from '../../core/util/useEffectWithoutMount';
import getBicycleFilterValues from '../util/getBicycleFilterValues';
import type { Bicycle } from '../types';
import BicycleSearchTable from './BicycleSearchTable';
import VehicleSearch from './VehicleSearch';
import styles from './BicycleSearch.module.scss';
import SparePartsFilters from './SparePartsSearch/SparePartsFilters';

export interface BicycleFilters {
    make: string[];
    model: string[];
    modelYear: string[];
    type: string[];
    category: string[];
}

export const filtersInitialValue = {
    make: [],
    model: [],
    modelYear: [],
    type: [],
    category: [],
};

const useBicycleSearch = (bicycles: Bicycle[]) => {
    const [params, setSearchParams] = useSearchParams();

    const search = params.get('search') ?? '';

    const allFilterValues = useMemo(() => {
        return bicycles.length > 0
            ? getBicycleFilterValues(bicycles, filtersInitialValue)
            : undefined;
    }, [bicycles]);

    const filters: BicycleFilters = useMemo(() => {
        return Object.keys(filtersInitialValue).reduce((result, key) => {
            const filterValue = allFilterValues
                ? params
                      .getAll(key)
                      .filter(value =>
                          allFilterValues[key as keyof BicycleFilters].includes(
                              value
                          )
                      )
                : params.getAll(key);
            return {
                ...result,
                [key]: filterValue,
            };
        }, filtersInitialValue);
    }, [params, allFilterValues]);

    const setSearch = useCallback(
        (value: string) => {
            params.set('search', value);
            setSearchParams(params, { preventScrollReset: true });
        },
        [params, setSearchParams]
    );

    const setFilters = useCallback(
        (newFilters: BicycleFilters) => {
            const keys = Object.keys(
                filtersInitialValue
            ) as (keyof BicycleFilters)[];

            for (const key of keys) {
                const values = newFilters[key];
                if (values.length) {
                    for (const value of values) {
                        params.set(key, value);
                    }
                } else {
                    params.delete(key);
                }
            }

            setSearchParams(params, { preventScrollReset: true });
        },
        [params, setSearchParams]
    );

    return { search, setSearch, filters, setFilters };
};

const BicycleSearch = () => {
    const { companyCode } = useCurrentStore();
    const isIntercycle = companyCode === 'ic';

    const { products: vehicles, loading, error } = useMetaProducts();
    const { search, setSearch, filters, setFilters } =
        useBicycleSearch(vehicles);

    const resetFilters = () => {
        setFilters(filtersInitialValue);
        setSearch('');
    };

    const [areFiltersActive, filteredVehicles] = filterBicycles(
        searchInVehicles(vehicles, sparePartSearchFields, search),
        filters
    );

    const pageSize = 24;
    const [page, setPage] = useState(1);
    useEffectWithoutMount(() => {
        setPage(1);
    }, [filteredVehicles.length]);

    return (
        <>
            <div className={styles.header}>
                <Container>
                    <BreadcrumbsViews
                        rootLinks={[
                            {
                                messageId: !isIntercycle
                                    ? 'spare parts search'
                                    : 'bicycle search',
                                to: '/spare-parts',
                            },
                        ]}
                    />
                </Container>
            </div>
            <Container pageContent>
                <VehicleSearch
                    vehicleFilters={
                        <SparePartsFilters
                            bicycles={vehicles}
                            filters={filters}
                            setFilters={setFilters}
                            search={search}
                            setSearch={setSearch}
                            resetFilters={resetFilters}
                            areFiltersActive={areFiltersActive}
                        />
                    }
                    vehiclesTable={
                        error ? (
                            <UnhandledError>{error}</UnhandledError>
                        ) : (
                            <BicycleSearchTable
                                bicycles={filteredVehicles
                                    .slice()
                                    .sort(bicycleSorter)
                                    .slice(
                                        (page - 1) * pageSize,
                                        page * pageSize
                                    )}
                            />
                        )
                    }
                    loading={loading}
                    filteredVehicles={filteredVehicles}
                    pageSize={pageSize}
                    page={page}
                    setPage={setPage}
                    title={isIntercycle ? 'all types' : 'all products'}
                />
            </Container>
        </>
    );
};

export default memo(BicycleSearch);
